/**
 * Quiet, dismissible affordance shown when a new version is known available but
 * auto-update is broken on this install. Two triggers, both from the main
 * process:
 * - `updateFallbackActive`: check/download failed >= threshold consecutive
 *   times while an update was known available.
 * - `installFailureDetected`: a previous session downloaded an update but
 *   restarts keep coming back on the old version (silent install failure).
 * It links to the manual download so stuck users can still get the update.
 *
 * - Hidden while status is 'downloaded' — that state belongs exclusively to
 *   UpdateReadyPrompt (which carries its own manual-download link when
 *   installs are failing). See UpdateToasts.singleOwnership.test.tsx.
 * - Dismissal persists PER VERSION (localStorage) so we don't nag.
 * - Fires `ui:update_fallback_shown` once per session and
 *   `ui:update_fallback_download_clicked` on click.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, Download, X } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { useAutoUpdatesStore } from '../../../store/autoUpdatesStore';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import { openExternalUrl } from '../../help-feedback';

export const UPDATE_DOWNLOAD_URL = 'https://jamsql.com/download/';
const DISMISS_KEY = 'jamsql:update-fallback-dismissed';

/** Hint text per detected macOS install issue (see UpdateInstallIssue). */
export function macInstallIssueHint(
  issue: 'read-only-location' | 'not-in-applications' | null
): string | null {
  switch (issue) {
    case 'read-only-location':
      return 'The app is running from a read-only location (like the downloaded disk image). Move it to the Applications folder so updates can install.';
    case 'not-in-applications':
      return 'Tip: move Jam SQL Studio to the Applications folder so updates can install reliably.';
    default:
      return null;
  }
}

/** Fire the "shown" event at most once per app session. */
let fallbackShownTracked = false;

function isDesktop(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    navigator.userAgent.includes('Electron') &&
    !!window.electronAPI?.autoUpdates
  );
}

function readDismissedVersion(): string | null {
  try {
    return window.localStorage.getItem(DISMISS_KEY);
  } catch {
    return null;
  }
}

function writeDismissedVersion(version: string): void {
  try {
    window.localStorage.setItem(DISMISS_KEY, version);
  } catch {
    // Ignore storage failures — worst case the banner reappears next session.
  }
}

export function UpdateFallbackBanner() {
  const { state } = useAutoUpdatesStore();
  const { track } = useAnalytics();
  const [dismissedVersion, setDismissedVersion] = useState<string | null>(() =>
    readDismissedVersion()
  );
  const shownFiredRef = useRef(false);

  const version = state.updateInfo?.version ?? null;
  const visible =
    isDesktop() &&
    (state.updateFallbackActive || state.installFailureDetected) &&
    state.status !== 'downloaded' &&
    version !== null &&
    dismissedVersion !== version;
  const hint = macInstallIssueHint(state.macInstallIssue);

  // Fire the "shown" event once per session when the banner first appears.
  useEffect(() => {
    if (!visible || shownFiredRef.current || fallbackShownTracked) return;
    shownFiredRef.current = true;
    fallbackShownTracked = true;
    track('ui:update_fallback_shown');
  }, [visible, track]);

  const handleDownload = useCallback(() => {
    track('ui:update_fallback_download_clicked', { source: 'banner' });
    void openExternalUrl(UPDATE_DOWNLOAD_URL);
  }, [track]);

  const handleDismiss = useCallback(() => {
    if (version) {
      writeDismissedVersion(version);
      setDismissedVersion(version);
    }
  }, [version]);

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-4 right-4 z-50 w-80 rounded-lg border border-amber-300 bg-background shadow-lg p-4 dark:border-amber-800"
      data-testid="update-fallback-banner"
    >
      <div className="space-y-3">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-warning mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <span className="text-sm font-medium block">Update available</span>
            <span className="text-xs text-muted-foreground block">
              Version {version} is ready, but automatic updates aren&apos;t working on this
              install. Download it manually to stay up to date.
            </span>
            {hint && (
              <span
                className="text-xs text-muted-foreground block mt-1"
                data-testid="update-fallback-mac-hint"
              >
                {hint}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={handleDismiss}
            className="text-muted-foreground hover:text-foreground transition-colors -mt-1 -mr-1 p-1"
            aria-label="Dismiss"
            data-testid="update-fallback-dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <Button
          size="sm"
          className="w-full"
          onClick={handleDownload}
          data-testid="update-fallback-download"
        >
          <Download className="mr-1.5 h-3.5 w-3.5" />
          Download update
        </Button>
      </div>
    </div>
  );
}
